import React, { useState, useRef, useEffect } from 'react';
import { Hash, MapPin, AtSign, X } from 'lucide-react';
import { User } from '../../types';

interface CaptionEditorProps {
  caption: string;
  onCaptionChange: (caption: string) => void;
  hashtags: string[];
  onHashtagsChange: (hashtags: string[]) => void;
  location: string;
  onLocationChange: (location: string) => void;
  taggedUsers: User[];
  onTaggedUsersChange: (users: User[]) => void;
  availableUsers?: User[];
}

export const CaptionEditor: React.FC<CaptionEditorProps> = ({
  caption,
  onCaptionChange,
  hashtags,
  onHashtagsChange,
  location,
  onLocationChange,
  taggedUsers,
  onTaggedUsersChange,
  availableUsers = []
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);

  const handleCaptionChange = (value: string) => {
    onCaptionChange(value);
    
    // Extract hashtags
    const hashtagMatches = value.match(/#\w+/g) || [];
    const extractedHashtags = hashtagMatches.map(tag => tag.substring(1));
    onHashtagsChange(extractedHashtags);

    // Check for @ mentions
    const atPosition = value.lastIndexOf('@', cursorPosition);
    if (atPosition !== -1) {
      const searchText = value.substring(atPosition + 1, cursorPosition);
      if (searchText.length > 0 && !searchText.includes(' ')) {
        setUserSearchQuery(searchText);
        setShowUserSuggestions(true);
      } else {
        setShowUserSuggestions(false);
      }
    } else {
      setShowUserSuggestions(false);
    }
  };

  const handleUserSelect = (user: User) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const atPosition = caption.lastIndexOf('@', cursorPosition);
    const beforeAt = caption.substring(0, atPosition);
    const afterCursor = caption.substring(cursorPosition);
    
    const newCaption = `${beforeAt}@${user.username} ${afterCursor}`;
    onCaptionChange(newCaption);
    
    if (!taggedUsers.find(u => u.id === user.id)) {
      onTaggedUsersChange([...taggedUsers, user]);
    }
    
    setShowUserSuggestions(false);
    textarea.focus();
  };

  const removeTaggedUser = (userId: string) => {
    onTaggedUsersChange(taggedUsers.filter(user => user.id !== userId));
  };

  const filteredUsers = availableUsers.filter(user =>
    user.username.toLowerCase().includes(userSearchQuery.toLowerCase()) &&
    !taggedUsers.find(tagged => tagged.id === user.id)
  );

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [caption]);

  return (
    <div className="space-y-4">
      {/* Caption Input */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={caption}
          onChange={(e) => {
            setCursorPosition(e.target.selectionStart);
            handleCaptionChange(e.target.value);
          }}
          onSelect={(e) => setCursorPosition((e.target as HTMLTextAreaElement).selectionStart)}
          placeholder="Write a caption... Use # for hashtags and @ to tag friends"
          className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[100px]"
          maxLength={2200}
        />
        
        {/* User Suggestions */}
        {showUserSuggestions && filteredUsers.length > 0 && (
          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto">
            {filteredUsers.slice(0, 5).map((user) => (
              <button
                key={user.id}
                onClick={() => handleUserSelect(user)}
                className="w-full flex items-center p-3 hover:bg-gray-50 text-left"
              >
                <img
                  src={user.profileImage}
                  alt={user.username}
                  className="w-8 h-8 rounded-full mr-3"
                />
                <div>
                  <p className="font-medium text-sm">{user.username}</p>
                  <p className="text-xs text-gray-500">{user.displayName}</p>
                </div>
              </button>
            ))}
          </div>
        )}
        
        <div className="flex justify-between items-center mt-2">
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <span className="flex items-center">
              <Hash className="h-4 w-4 mr-1" />
              {hashtags.length} hashtags
            </span>
            <span className="flex items-center">
              <AtSign className="h-4 w-4 mr-1" />
              {taggedUsers.length} tagged
            </span>
          </div>
          <span className="text-sm text-gray-500">
            {caption.length}/2200
          </span>
        </div>
      </div>

      {/* Location Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MapPin className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={location}
          onChange={(e) => onLocationChange(e.target.value)}
          placeholder="Add location"
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>

      {/* Tagged Users */}
      {taggedUsers.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Tagged Users</h4>
          <div className="flex flex-wrap gap-2">
            {taggedUsers.map((user) => (
              <div
                key={user.id}
                className="flex items-center bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm"
              >
                <img
                  src={user.profileImage}
                  alt={user.username}
                  className="w-5 h-5 rounded-full mr-2"
                />
                <span>@{user.username}</span>
                <button
                  onClick={() => removeTaggedUser(user.id)}
                  className="ml-2 text-purple-600 hover:text-purple-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hashtags Preview */}
      {hashtags.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Hashtags</h4>
          <div className="flex flex-wrap gap-2">
            {hashtags.map((hashtag, index) => (
              <span
                key={index}
                className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm"
              >
                #{hashtag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};